import { useState } from "react";
import { useGetList, useRecordContext, useTranslate } from "ra-core";
import { Link } from "react-router";
import { DateField } from "@/components/admin/date-field";
import { EditButton } from "@/components/admin/edit-button";
import { NumberField } from "@/components/admin/number-field";
import { RecordField } from "@/components/admin/record-field";
import { ReferenceField } from "@/components/admin/reference-field";
import { Show } from "@/components/admin/show";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, ArrowDownLeft, ArrowUpRight, Copy } from "lucide-react";
import { PaymentStatusBadge } from "./PaymentStatusBadge";
import { MarkPaymentPaidSheet } from "../shared/MarkPaymentPaidSheet";
import { TransactionDirectionBadge } from "../shared/TransactionDirectionBadge";
import type { Payment, Transaction } from "../../types";

const DuplicateButton = () => {
  const translate = useTranslate();
  const record = useRecordContext<Payment>();
  if (!record) return null;
  return (
    <Button asChild variant="outline" size="sm" className="gap-2">
      <Link to={`/payments/create?duplicate_from=${record.id}`}>
        <Copy className="h-4 w-4" />
        {translate("resources.payments.action.duplicate", { _: "Дублировать" })}
      </Link>
    </Button>
  );
};

export const PaymentShow = () => (
  <Show
    actions={
      <div className="flex justify-end gap-2">
        <DuplicateButton />
        <EditButton />
      </div>
    }
  >
    <div className="space-y-4">
      <MarkPaidAction />
      <Card>
        <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <RecordField
            source="direction"
            render={(p) => <TransactionDirectionBadge direction={p.direction} />}
          />
          <RecordField source="amount" field={NumberField} />
          <RecordField
            source="vendor_name"
            label="resources.payments.fields.recipient"
            render={(p) =>
              p.vendor_name ? (
                <span>{p.vendor_name}</span>
              ) : p.contract_id ? (
                <ReferenceField
                  record={p as { id: number; contract_id: number | null }}
                  source="contract_id"
                  reference="contracts"
                  link="show"
                />
              ) : (
                <span className="text-muted-foreground">—</span>
              )
            }
          />
          <RecordField source="category_id">
            <ReferenceField source="category_id" reference="transaction_categories" link={false} />
          </RecordField>
          <RecordField source="planned_date" field={DateField} />
          <RecordField source="paid_date" field={DateField} />
          <RecordField
            source="status"
            render={(payment) => <PaymentStatusBadge status={payment.status} />}
          />
          <RecordField source="reminder_sent_at" field={DateField} />
          <RecordField source="comment" className="md:col-span-2" />
        </CardContent>
      </Card>
      <LinkedTransaction />
    </div>
  </Show>
);

const MarkPaidAction = () => {
  const translate = useTranslate();
  const payment = useRecordContext<Payment>();
  const [open, setOpen] = useState(false);
  if (!payment || payment.status === "paid") return null;
  const isExpense = payment.direction === "expense";
  return (
    <div>
      <Button onClick={() => setOpen(true)}>
        <CheckCircle2 className="h-4 w-4 mr-2" />
        {isExpense
          ? translate("finance.markPaid.actionExpense", { _: "Провести выплату" })
          : translate("finance.markPaid.action", { _: "Принять оплату" })}
      </Button>
      <MarkPaymentPaidSheet open={open} onOpenChange={setOpen} payment={payment} />
    </div>
  );
};

const LinkedTransaction = () => {
  const translate = useTranslate();
  const payment = useRecordContext<Payment>();
  const { data } = useGetList<Transaction>(
    "transactions",
    {
      pagination: { page: 1, perPage: 1 },
      sort: { field: "id", order: "DESC" },
      filter: { payment_id: payment?.id },
    },
    { enabled: !!payment?.id },
  );
  const tx = data?.[0];
  if (!tx) return null;
  const isIncome = tx.direction === "income";
  const Icon = isIncome ? ArrowDownLeft : ArrowUpRight;
  const color = isIncome ? "text-emerald-600" : "text-rose-600";
  return (
    <Card>
      <CardContent className="px-4 py-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Icon className={`h-4 w-4 ${color}`} />
          <span className="text-sm">
            {translate("finance.markPaid.transactionLabel", {
              _: "Связанная транзакция",
            })}
            : {Number(tx.amount).toLocaleString("ru-RU")} ₽ ·{" "}
            {new Date(tx.occurred_at).toLocaleDateString("ru-RU")}
          </span>
        </div>
        <Button asChild variant="ghost" size="sm">
          <Link to={`/transactions/${tx.id}/show`}>
            {translate("ra.action.show", { _: "Открыть" })}
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
};
